import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import Input from "@/app/components/ui/Inputs/Input";
import NumberInput from "@/app/components/ui/Inputs/NumberInput";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useReminderCreationAPI } from "@/app/hooks/api_hooks/reminder/useReminderCreationAPI";
import { useReminderDetailsAPI } from "@/app/hooks/api_hooks/reminder/useReminderDetailsAPI";
import { useReminderTypeAPI } from "@/app/hooks/api_hooks/reminder/useReminderTypeAPI";
import { useReminderUpdationAPI } from "@/app/hooks/api_hooks/reminder/useReminderUpdationAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import clsx from "clsx";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

export interface IAddReminderCreation {
  user_id?: string;
  pet_id?: string;
  reminder_name: string;
  reminder_type: string;
  reminder_id?: string;
  due_date: string;
  expiry_days: any;
  comments: string;
}
const RemindersCreation = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const { forOnlyNumbersWithDecimal, forAllAcceptWithOutStartWithDotAndSpace } = useFormValidations();
  const { customerId, petId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;

  const stringConstants = useStringConstants();

  const { execute: fetchReminder, reminder } = useReminderTypeAPI();
  const { execute: fetchReminderDetails, reminderDetails } = useReminderDetailsAPI();
  const { execute: createReminder } = useReminderCreationAPI();
  const { execute: updateReminderAPI } = useReminderUpdationAPI();
  const [loader, setLoader] = useState<boolean>(false);
  const [params, _setparams] = useSearchParams();

  //Default State
  const formHook = useForm<IAddReminderCreation>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    const reminderId = params.get("reminder_id");
    if (reminderId) {
      fetchReminderDetails(reminderId);
    }
  }, [params]);

  useEffect(() => {
    if (reminderDetails) {
      formHook.setValue("comments", reminderDetails?.comments);
      formHook.setValue("due_date", reminderDetails?.due_date);
      formHook.setValue("expiry_days", reminderDetails?.expiry_days);
      formHook.setValue("reminder_name", reminderDetails?.reminder_name);
      formHook.setValue("reminder_type", reminderDetails?.reminder_type);
    }
  }, [reminderDetails]);

  formHook.watch("due_date");
  const reminderItem = useSelectMenuReducer(reminder, "name", "id");

  const handelAddReminder = () => {
    navigate(`${getRouteKey("REMINDERS_TAB", "url")}/${customerId}/${petId}`);
  };
  const onSubmit = (data: IAddReminderCreation) => {
    setLoader(true);
    const constructedBody: IAddReminderCreation = {
      pet_id: petId,
      user_id: customerId,
      reminder_id: params.get("reminder_id") ? params.get("reminder_id") : undefined,
      comments: data.comments,
      due_date: data.due_date,
      expiry_days: 30,
      reminder_name: data.reminder_name,
      reminder_type: data.reminder_type,
    };
    if (params.get("reminder_id")) {
      updateReminderAPI(constructedBody).then(({ status }) => {
        setLoader(false);
        if (status) {
          navigate(`${getRouteKey("REMINDERS_TAB", "url")}/${customerId}/${petId}`);
        }
      });
    } else {
      createReminder(constructedBody).then(({ status }) => {
        setLoader(false);
        if (status) {
          navigate(`${getRouteKey("REMINDERS_TAB", "url")}/${customerId}/${petId}`);
        }
      });
    }
  };

  useEffect(() => {
    fetchReminder();
  }, []);

  const handelBackToReminderList = () => {
    navigate(`${getRouteKey("REMINDERS_TAB", "url")}/${customerId}/${petId}`);
  };

  const isReminder_id = params.get("reminder_id");

  return (
    <>
      <p
        onClick={() => handelAddReminder()}
        className="text-blue-600 font-semibold cursor-pointer text-[10px] sm:text-xs 2xl:text-sm  py-2"
      >
        Reminder Setup
      </p>
      <div className="bg-white max-h-screen">
        <form onSubmit={formHook.handleSubmit(onSubmit)}>
          <div className="py-2">
            <div className="flex justify-between gap-14  sm:justify-start sm:items-center sm:gap-20 flex-row  p-3 ">
              <p className="text-[10px] sm:text-xs w-6 sm:w-24">Name</p>
              <div className="w-auto sm:w-1/6">
                <Input
                  className="text-xs"
                  placeholder="Reminder name"
                  register={formHook.register("reminder_name", {
                    required: true,
                    ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                  })}
                  fieldError={formHook.formState.errors.reminder_name}
                  errorMessages={[{ message: "", type: "required" }, forAllAcceptWithOutStartWithDotAndSpace.errors]}
                />
              </div>
            </div>
            <div className="w-auto sm:w-full sm:grid sm:grid-cols-2 ">
              <div className="flex justify-between gap-14  sm:justify-start sm:items-center sm:gap-20 flex-row bg-white  p-3 w-full">
                <p className="text-[10px] sm:text-xs w-6 sm:w-24">Reminder Type</p>
                <div className="w-auto sm:w-1/3">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("reminder_type", item.title);
                      }
                    }}
                    fieldError={formHook.formState.errors.reminder_type}
                    register={formHook.register("reminder_type", { required: true })}
                    selectItems={reminderItem}
                    placeholder="Select Reminder"
                    showTooltips={true}
                    showTypedErrors={false}
                    showDropdownIcon={true}
                    defaultSelected={reminderItem?.filter((oc) => oc.title === formHook.watch("reminder_type"))[0]}
                    listBoxClassName="w-full"
                    className="text-gray-800 w-10"
                    containerClassName="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="w-full sm:grid sm:grid-cols-2 ">
              <div className="flex justify-between gap-14  sm:justify-start sm:items-center sm:gap-20 flex-row bg-white  p-3 w-full">
                <p className="text-[10px] sm:text-xs w-6  sm:w-24">Due Date</p>
                <div className="w-auto sm:w-1/2">
                  <PWLCalendarComponent
                    showFutureYears
                    allowPastDaySelection={false}
                    errorMessages={[{ message: "Date is required", type: "required" }]}
                    register={formHook.register("due_date", {
                      required: true,
                    })}
                    disableDatesBeforeDate={format(new Date(Date.now()), stringConstants?.fieldDOBFormat)}
                    showTypedErrors={false}
                    showLeftNav={false}
                    fieldError={formHook.formState.errors.due_date}
                    onSelectDate={(date) => {
                      formHook.setValue("due_date", format(date, "yyyy-MM-dd"));
                    }}
                    defaultSelectedDate={formHook.watch("due_date")}
                    className=""
                  />
                </div>
              </div>
            </div>
            {/* <div className="w-full grid grid-cols-2 ">
              <div className="flex items-center gap-20 flex-row bg-white  p-3 w-full">
                <p className="text-xs w-24">Expire in </p>
                <div className="w-2/3 flex gap-3 items-center">
                  <NumberInput
                    className="text-xs"
                    placeholder="Enter Days"
                    maxLength={2}
                    register={formHook.register(`expiry_days`, {
                      required: false,
                      ...forOnlyNumbersWithDecimal.validations,
                      validate: (value: any) => {
                        const trimmedValue = value.replace(/^0+/, "") || "0";
                        formHook.setValue(`expiry_days`, trimmedValue);
                        return null;
                      },
                    })}
                    fieldError={formHook.formState.errors.expiry_days}
                    errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                  />
                  <span className="text-[10px] sm:text-xs">Days</span>
                </div>
              </div>
            </div> */}

            <div className="flex justify-between gap-14  sm:justify-start sm:items-center sm:gap-20 flex-row bg-white  p-3 ">
              <p className="text-[10px] sm:text-xs w-6 sm:w-24">Comment</p>
              <div className="w-full pl-0 sm:pl-2">
                <textarea
                  style={{
                    overflowY: "scroll", // Always show vertical scrollbar
                    scrollbarWidth: "thin", // Works for Firefox
                  }}
                  rows={3}
                  className={clsx(`rounded-md w-full h-full  border-gray-300 text-xs `)}
                  placeholder="Add Comment if any..."
                  {...formHook.register(`comments`, { required: false, minLength: 1 })}
                />
              </div>
            </div>
          </div>

          <div className={`w-full  flex justify-end gap-3 py-4`}>
            <button
              type="button"
              onClick={() => handelBackToReminderList()}
              className="text-[10px] sm:text-xs  w-auto justify-center rounded-md bg-[#b1b5bb] px-10 py-2 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-white bg-[#193A6A] w-auto px-10 py-2 rounded-lg font-semibold text-[10px] sm:text-xs"
              disabled={loader}
            >
              {loader ? (
                <div className="">
                  {" "}
                  <CircleSpinnerLoader h="20" w="20" />
                </div>
              ) : isReminder_id ? (
                "Update Reminder"
              ) : (
                "Save Reminder"
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};
export default RemindersCreation;
